/**
 * NOTE on redux / sample data:
 *  - `payment.getPaymentInfo()` doesn't exist on web (the payment duck only
 *    has subscribe/checkout, per the payment-stub scope agreed earlier).
 *    Subscription status and renewal date already come from
 *    customerProfile.data (has_active_subscription, renewal_date), so this
 *    screen reads those directly instead of making an extra fetch.
 *  - `deleteAccount` is a new module built specifically for this screen — no
 *    delete-account capability existed anywhere in the redux store. Its API
 *    endpoint (/users/delete-account/) follows existing naming conventions
 *    but hasn't been confirmed against the real backend.
 *  - Dashboard stats are built from the same endpoints the other customer
 *    screens use (no dedicated stats endpoint exists):
 *      Total Referrals   — customer-referrals + business-referrals counts
 *      Total Earned $    — earned_points / 100 (points are 100 to the dollar)
 *      Total Saved $     — customerSavings module (backend
 *                          /coupons/customers/<id>/savings/, lifetime dollars
 *                          saved on ACCEPTED redemptions)
 *      Total Used Coupons— apply-coupon list count (what "Used Coupons" lists)
 *      Monthly Coupons   — coupon_limit vs. coupons applied this calendar month
 *      Location          — profile city, else the city picked in the header
 *    Phone / city / the applied-coupon date are read from a few likely field
 *    names (see pickField) since their exact API names aren't confirmed.
 *  - Visual redesign (Sept 2026): matches new mockups — the top navbar shown
 *    in those mockups is rendered by the app's shared layout, not this
 *    component. The profile photo, promo banner artwork, and the faint
 *    wavy-line watermark behind the profile details are static creative
 *    assets under ../../assets/images/ — swap them for real ones (or wire
 *    the profile photo to an actual avatar field) as those become available.
 *  - Logout now goes through a confirmation modal (same overlay/content
 *    styles as the delete-account modal); `handleLogout` is the confirm action.
 */
import React, { useCallback, useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import {
  Award,
  ChevronRight,
  DollarSign,
  Edit2,
  FileText,
  Gift,
  Heart,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Share2,
  Shield,
  Smartphone,
  Tag,
  Trash2,
  User,
} from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { generateBranchLink } from 'service/branchService';

import { Spinner, SeoHead } from 'components';
import { actions as appliedCouponsActions } from 'coupon-common/src/modules/customerCoupons/applied';
import { actions } from 'coupon-common/src/modules/customerProfile';
import { actions as savingsActions } from 'coupon-common/src/modules/customerSavings';
import { actions as authActions } from 'coupon-common/src/modules/auth';
import { actions as userDeleteActions } from 'coupon-common/src/modules/deleteAccount';
import { actions as businessReferralsActions } from 'coupon-common/src/modules/referrals/businesses';
import { actions as customerReferralsActions } from 'coupon-common/src/modules/referrals/customers';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import profilePhoto from '../../assets/images/profilePhoto.webp';
import promoBanner from '../../assets/images/referralPointsBanner.png';
import PremiumCard from '../../components/MembershipCard/PremiumCard';
import StandardCard from '../../components/MembershipCard/StandardCard';
import { RouteNames } from '../../constants';
import messages from './messages';
import { CouponPointsIcon, MonthlyCouponsIcon, PremiumIcon, TotalCouponsIcon } from './Staticons';
import styles from './MyAccount.module.scss';

// First non-empty value among `keys` on `obj` — for fields whose exact API
// name isn't confirmed (see file header note).
const pickField = (obj, keys) => keys.map(key => obj?.[key]).find(value => value !== undefined && value !== null && value !== '');

// Paginated list total: DRF's `count`, else however many rows loaded.
const listTotal = list => list?.count ?? list?.results?.length ?? 0;

const isThisMonth = value => {
  const date = value && new Date(value);
  const now = new Date();
  return !!date && date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
};

const MyAccount = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    fetching,
    fetched,
    data: {
      earned_points: earnedPoints,
      is_business_marketer: isBusinessMarketer,
      has_active_subscription: hasActiveSubscription,
      renewal_date: renewalDate,
      first_name: firstName,
      last_name: lastName,
      referral_code: referralCode,
      email,
      total_points: totalPoints,
      coupon_limit: couponLimit,
    } = {},
    data: profileData,
    error,
  } = useSelector(state => state.customerProfile);

    useEffect(() => {
    console.log('[MyAccount] customerProfile slice:', { fetching, fetched, error, profileData });
    console.log('[MyAccount] totalPoints raw:', profileData?.totalPoints, typeof profileData?.totalPoints);
    console.log('[MyAccount] profile keys:', profileData ? Object.keys(profileData) : null);
  }, [fetching, fetched, error, profileData]);

  const customerReferrals = useSelector(state => state.referrals.customers?.data);
  const businessReferrals = useSelector(state => state.referrals.businesses?.data);
  const appliedCoupons = useSelector(state => state.customerCoupons.applied);
  const activeCity = useSelector(state => state.cities?.activeCity);
  const savings = useSelector(state => state.customerSavings);

  const customerId = profileData?.id;
  const totalReferrals = listTotal(customerReferrals) + listTotal(businessReferrals);
  const totalUsedCoupons = appliedCoupons?.result?.count ?? appliedCoupons?.result?.results?.length ?? 0;
  const appliedThisMonth = (appliedCoupons?.result?.results || [])
    .map(id => appliedCoupons.items?.[id])
    .filter(coupon => coupon && isThisMonth(pickField(coupon, ['created_at', 'created', 'created_on', 'applied_at'])))
    .length;
  const phone = pickField(profileData, ['phone_number', 'phone', 'mobile_number']);
  const location = pickField(profileData, ['city_name', 'city']) || activeCity;

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [branchLink, setBranchLink] = useState('');

  const renewalDateParsed = renewalDate ? new Date(renewalDate) : null;
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'My Account';
  const totalEarnedDollars = ((totalPoints || 0) / 100).toFixed(0);
  // Lifetime dollars saved — backend returns total_saved as a "0.00"-style string.
  const totalSavedDollars = Number(savings?.data?.total_saved || 0).toFixed(2);
  // Same Branch referral link ShareReferralCode turns into a QR code; the
  // plain signup URL covers the gap until Branch responds (or if it fails).
  const qrValue =
    branchLink || (referralCode ? `https://mycouponbook.ca/signup?ref=${referralCode}` : 'https://mycouponbook.ca');

  useEffect(() => {
    if (!referralCode) return undefined;

    let cancelled = false;
    generateBranchLink(referralCode).then(link => {
      if (link && !cancelled) setBranchLink(link);
    });

    return () => {
      cancelled = true;
    };
  }, [referralCode]);

  const getCustomerProfile = useCallback(() => {
    dispatch(actions.getCustomerProfile());
  }, [dispatch]);

  useEffect(() => {
    getCustomerProfile();
    dispatch(customerReferralsActions.getCustomersList());
    dispatch(businessReferralsActions.getBusinessesList());
    dispatch(appliedCouponsActions.getApplyCouponList());
  }, [getCustomerProfile, dispatch]);

  // Needs the customer id from the profile, so it runs once that has loaded.
  useEffect(() => {
    if (customerId) {
      dispatch(savingsActions.getCustomerSavings(customerId));
    }
  }, [customerId, dispatch]);

  const handleDeleteUser = async () => {
    setIsDeleting(true);
    const result = await dispatchAsAsync(userDeleteActions.deleteUser(), dispatch);
    setIsDeleting(false);
    if (!result) {
      setDeleteModalVisible(false);
    }
  };

  const handleLogout = () => {
    dispatch(authActions.logout());
    navigate(RouteNames.SIGN_IN);
  };

  const goToPrivacyPolicy = () => navigate(RouteNames.PRIVACY_POLICY);
  const goToTermsOfService = () => navigate(RouteNames.TERMS_OF_SERVICE);
  const goToGuidelines = () => navigate(RouteNames.GUIDELINES);
  const goToSubscribe = () => navigate(RouteNames.PAYMENT_FORM);
  const goToPreferences = () => navigate(RouteNames.PREFERENCES);
  const goToUsedCoupons = () => navigate(RouteNames.USED_COUPONS_HISTORY);
  const goToFavourites = () => navigate(RouteNames.FAVOURITES);
  const goToMyReferrals = () => navigate(RouteNames.REFERRALS_CUSTOMERS);

  // Same Web Share API (native share sheet) with clipboard-copy fallback
  // ShareRefCodeButton already uses for the customer-side referral link.
  const handleShareReferral = async () => {
    if (!referralCode) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on My Couponbook',
          text: `Use my referral code ${referralCode} to get started on My Couponbook!`,
          url: qrValue,
        });
        return;
      } catch (err) {
        // User cancelled the native share sheet, or it failed — fall through
        // to clipboard copy below.
      }
    }

    try {
      await navigator.clipboard.writeText(qrValue);
    } catch (err) {
      console.warn('Could not copy referral link to clipboard', err);
    }
  };

  if (error) {
    return (
      <div styleName="page">
        <p styleName="state-message">Something went wrong loading your account.</p>
      </div>
    );
  }

  if (!fetched) {
    return (
      <div styleName="page" style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner show size="lg" />
      </div>
    );
  }

  return (
    <div styleName="page">
      <SeoHead title="My Account" noIndex />
      <div styleName="wrap">
        <div styleName="header-row">
          <div styleName="title-block">
            <div>
              <h1 styleName="page-title">
                <FormattedMessage
                  {...messages.pageTitle}
                  values={{
                    strong: chunks => <strong>{chunks}</strong>,
                  }}
                />
              </h1>
              <p styleName="page-subtitle">
                <FormattedMessage {...messages.pointsAssignmentText} />
              </p>
            </div>

            <div styleName="stats-row">
              <div styleName="stat-inline">
                <Gift size={20} />
                <div styleName="stat-inline-body">
                  <span styleName="stat-inline-value">{totalReferrals}</span>
                  <span styleName="stat-inline-label">{intl.formatMessage(messages.totalReferralsLabel)}</span>
                </div>
              </div>
              {/* <div styleName="stat-inline">
                <DollarSign size={20} />
                <div styleName="stat-inline-body">
                  <span styleName="stat-inline-value">${totalEarnedDollars}</span>
                  <span styleName="stat-inline-label">{intl.formatMessage(messages.totalEarnedLabel)}</span>
                </div>
              </div> */}
              <div styleName="stat-inline">
                <Tag size={20} />
                <div styleName="stat-inline-body">
                  <span styleName="stat-inline-value">${totalSavedDollars}</span>
                  <span styleName="stat-inline-label">Total Saved</span>
                </div>
              </div>
            </div>
          </div>

          <div styleName="promo-banner">
            <img src={promoBanner} alt={intl.formatMessage(messages.newFeatureLaunchAlt)} styleName="promo-banner-img" />
          </div>
        </div>

        <div styleName="grid">
        <div styleName="grid-top">
          <div styleName="card profile-card">
            <div styleName="profile-card-bg" />

            <button type="button" styleName="edit-btn" onClick={goToPreferences} aria-label={intl.formatMessage(messages.editProfileText)}>
              <Edit2 size={16} />
            </button>

            <div styleName="profile-photo-wrap">
              <img src={profilePhoto} alt={fullName} styleName="profile-photo" />
              <div styleName="profile-photo-scrim" />
              <span styleName="profile-photo-name">{firstName || fullName}</span>
            </div>

            <div styleName="profile-details-wrap">
              <div styleName="profile-details">
                <div styleName="profile-row">
                  <User size={18} />
                  <span styleName="profile-row-text">{fullName}</span>
                </div>
                <div styleName="profile-row">
                  <Mail size={18} />
                  <span styleName="profile-row-text">{email || '—'}</span>
                </div>
                {phone && (
                  <div styleName="profile-row">
                    <Phone size={18} />
                    <span styleName="profile-row-text">{phone}</span>
                  </div>
                )}
                {location && (
                  <div styleName="profile-row">
                    <MapPin size={18} />
                    <span styleName="profile-row-text">{location}</span>
                  </div>
                )}
              </div>

              {isBusinessMarketer && (
                <div styleName="status-block">
                  <Award size={20} />
                  <span styleName="status-block-text">
                    <FormattedMessage
                      {...messages.yourStatusText}
                      values={{
                        statusText: (
                          <strong styleName="text-accent">
                            <FormattedMessage {...messages.statusText} />
                          </strong>
                        ),
                      }}
                    />
                  </span>
                </div>
              )}
            </div>
          </div>

          <div styleName="stat-tiles-outer">
          <div styleName="stat-tiles">
            <div styleName="stat-tile">
              <div>
                <p styleName="stat-tile-label">{intl.formatMessage(messages.couponPointsLabel)}</p>
                <p styleName="stat-tile-value">{totalPoints || 0}</p>
              </div>
              <CouponPointsIcon size={42} styleName="stat-tile-icon" />
            </div>
            <div styleName="stat-tile">
              <div>
                <p styleName="stat-tile-label">{intl.formatMessage(messages.totalUsedCouponsLabel)}</p>
                <p styleName="stat-tile-value">{totalUsedCoupons}</p>
              </div>
              <TotalCouponsIcon size={46} styleName="stat-tile-icon" />
            </div>
            <div styleName="stat-tile">
              <div>
                <p styleName="stat-tile-label">{intl.formatMessage(messages.monthlyCouponLabel)}</p>
                <p styleName="stat-tile-count-row">
                  <span>Count</span> <strong>{hasActiveSubscription || !couponLimit ? '∞' : couponLimit}</strong> — <span>Used</span>{' '}
                  <strong>{appliedThisMonth}</strong>
                </p>
              </div>
              <MonthlyCouponsIcon size={30} styleName="stat-tile-icon" />
            </div>
            {hasActiveSubscription ? (
              <div styleName="stat-tile premium-tile">
                <PremiumIcon size={20} />
                <span>{intl.formatMessage(messages.premiumMemberBadge)}</span>
              </div>
            ) : (
              <div styleName="stat-tile subscribe-tile">
                <div styleName="subscribe-tile-label">
                  <span>
                    <FormattedMessage
                      {...messages.increaseMonthlyCouponCountText}
                      values={{
                        small: chunks => <small>{chunks}</small>,
                      }}
                    />
                  </span>
                  <Award size={14} />
                </div>
                               <button
                  type="button"
                  styleName="subscribe-btn subscribe-btn-disabled"
                  disabled
                  aria-disabled="true"
                  data-tooltip="Coming Soon"
                >
                  <PremiumIcon size={16} />
                  <span>{intl.formatMessage(messages.subscribeToPremium)}</span>
                </button>
              </div>
            )}
          </div>
          </div>
        </div>

        <div styleName="grid-bottom">
          <div styleName="card membership-card">
            <div styleName="card-header-row">
              <h2 styleName="subtitle">
                <Award size={18} />
                {intl.formatMessage(messages.membershipTitle)}
              </h2>
              <button type="button" styleName="edit-btn" onClick={goToPreferences} aria-label={intl.formatMessage(messages.editProfileText)}>
                <Edit2 size={16} />
              </button>
            </div>

            {hasActiveSubscription ? (
              <PremiumCard
                businessName={fullName}
                points={String(totalPoints || 0)}
                qrValue={qrValue}
                renewalDate={renewalDateParsed ? intl.formatDate(renewalDateParsed) : '—'}
                autoRenewal={renewalDateParsed ? intl.formatDate(renewalDateParsed) : '—'}
              />
            ) : (
              <StandardCard businessName={fullName} points={String(totalPoints || 0)} qrValue={qrValue} onGetPremiumClick={goToSubscribe} />
            )}

            {referralCode && (
              <p styleName="base-text" style={{ textAlign: 'center', marginTop: '1rem', fontSize: '12px' }}>
                <FormattedMessage
                  {...messages.referralCodeInstructionText}
                  values={{
                    targetText: (
                      <strong styleName="text-accent">
                        <FormattedMessage {...messages.referralsText} />
                      </strong>
                    ),
                  }}
                />
              </p>
            )}

            {hasActiveSubscription && renewalDateParsed && (
              <p styleName="base-text" style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                <FormattedMessage
                  {...messages.yourRenewalDateText}
                  values={{
                    renewalDateText: (
                      <strong styleName="text-accent">
                        <FormattedMessage {...messages.renewalDate} values={{ renewalDate: renewalDateParsed }} />
                      </strong>
                    ),
                  }}
                />
              </p>
            )}
            {!hasActiveSubscription && referralCode && (
              <button
                type="button"
                styleName="referral-share-btn"
                style={{ marginTop: '2rem' }}
                onClick={handleShareReferral}
              >
                <Share2 size={14} />
                <span>Share Referral Link</span>
              </button>
            )}
          </div>

          <div styleName="card panel">
            <h2 styleName="subtitle">
              <Shield size={18} />
              {intl.formatMessage(messages.legalInfoTitle)}
            </h2>
            <div styleName="menu-list">
              <button type="button" styleName="menu-link" onClick={goToTermsOfService}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <Smartphone size={16} />
                  </span>
                  {intl.formatMessage(messages.userGuidelinesText)}
                </span>
                <ChevronRight size={16} />
              </button>
              <button type="button" styleName="menu-link" onClick={goToPrivacyPolicy}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <Lock size={16} />
                  </span>
                  {intl.formatMessage(messages.privacyPolicyText)}
                </span>
                <ChevronRight size={16} />
              </button>
              <button type="button" styleName="menu-link" onClick={goToTermsOfService}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <FileText size={16} />
                  </span>
                  {intl.formatMessage(messages.termsOfServiceText)}
                </span>
                <ChevronRight size={16} />
              </button>
              <button type="button" styleName="menu-link danger" onClick={() => setDeleteModalVisible(true)}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <Trash2 size={16} />
                  </span>
                  {intl.formatMessage(messages.deleteAccountText)}
                </span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div styleName="card panel">
            <h2 styleName="subtitle">
              <User size={18} />
              {intl.formatMessage(messages.accountMenuTitle)}
            </h2>
            <div styleName="menu-list">
              <button type="button" styleName="menu-link" onClick={goToUsedCoupons}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <Tag size={16} />
                  </span>
                  {intl.formatMessage(messages.usedCouponsMenuText)}
                </span>
                <ChevronRight size={16} />
              </button>
              <button type="button" styleName="menu-link" onClick={goToFavourites}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <Heart size={16} />
                  </span>
                  {intl.formatMessage(messages.favoritesMenuText)}
                </span>
                <ChevronRight size={16} />
              </button>
              <button type="button" styleName="menu-link" onClick={goToMyReferrals}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <Gift size={16} />
                  </span>
                  {intl.formatMessage(messages.myReferralsMenuText)}
                </span>
                <ChevronRight size={16} />
              </button>
              <button type="button" styleName="menu-link danger" onClick={() => setLogoutModalVisible(true)}>
                <span styleName="menu-link-label">
                  <span styleName="menu-link-icon">
                    <LogOut size={16} />
                  </span>
                  {intl.formatMessage(messages.logoutMenuText)}
                </span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
        </div>

        <p styleName="version-text">Build 179, version 3.0.1</p>
      </div>

      {deleteModalVisible && (
        <div styleName="modal-overlay" onClick={() => !isDeleting && setDeleteModalVisible(false)}>
          <div styleName="modal-content" onClick={e => e.stopPropagation()}>
            <h2 styleName="modal-title">Delete Account</h2>
            <p styleName="modal-message">Are you sure you want to delete your account? This cannot be undone.</p>
            <div styleName="modal-buttons">
              <button type="button" styleName="modal-btn-cancel" onClick={() => setDeleteModalVisible(false)} disabled={isDeleting}>
                Cancel
              </button>
              <button type="button" styleName="modal-btn-confirm" onClick={handleDeleteUser} disabled={isDeleting}>
                {isDeleting ? <Spinner show size="sm" /> : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {logoutModalVisible && (
        <div styleName="modal-overlay" onClick={() => setLogoutModalVisible(false)}>
          <div styleName="modal-content" onClick={e => e.stopPropagation()}>
            <h2 styleName="modal-title">Log Out</h2>
            <p styleName="modal-message">Are you sure you want to log out?</p>
            <div styleName="modal-buttons">
              <button type="button" styleName="modal-btn-cancel" onClick={() => setLogoutModalVisible(false)}>
                Cancel
              </button>
              <button type="button" styleName="modal-btn-confirm" onClick={handleLogout}>
                Yes, Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSSModule(MyAccount, styles, { allowMultiple: true });